(function () {
  'use strict';

  // Controller for snippets/common-carousel.liquid. Reads every behaviour off the
  // root's data attributes, which the caller supplies, so nothing is decided here.
  // The visible-slide count is owned by CSS and read back out, which keeps the
  // step, clamping and dot states in sync across breakpoints without pixel math.

  const SWIPE_THRESHOLD = 40;

  function collectElements(root) {
    return {
      track: root.querySelector('[data-carousel-track]'),
      viewport: root.querySelector('[data-carousel-viewport]'),
      slides: Array.from(root.querySelectorAll('[data-carousel-slide]')),
      prev: root.querySelector('[data-carousel-prev]'),
      next: root.querySelector('[data-carousel-next]'),
      dots: Array.from(root.querySelectorAll('[data-carousel-dot]')),
    };
  }

  // Reads the count CSS wants for the current breakpoint. The inline override is
  // cleared first so we read the stylesheet's value rather than our own cap.
  function readSlidesToShow(root) {
    root.style.removeProperty('--c-carousel-slides');
    const value = parseInt(getComputedStyle(root).getPropertyValue('--c-carousel-slides'), 10);
    return Math.max(1, value || 1);
  }

  // Never show more slots than there are images: an empty slot reads as a broken
  // layout, and with nothing to scroll the controls would be inert. Capping makes
  // the slides fill the width and flags the carousel static so the chrome hides.
  function applySlideCap(root, desired, count) {
    const effective = Math.min(desired, count);
    if (effective !== desired) root.style.setProperty('--c-carousel-slides', effective);
    root.dataset.static = count <= effective ? 'true' : 'false';
    return effective;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Opt-in staggered entrance: each slide fades and grows in as it enters the
  // viewport, slides in the same intersection batch stagger left to right, and the
  // counter resets after an idle gap so an isolated slide appears immediately.
  function setupSlideReveal(slides) {
    if (slides.length === 0) return;
    if (!('IntersectionObserver' in window)) {
      slides.forEach(function (slide) { slide.setAttribute('data-revealed', 'true'); });
      return;
    }
    const STEP_MS = 90;
    const BATCH_GAP_MS = 220;
    let batchCount = 0;
    let lastRevealAt = 0;
    const observer = new IntersectionObserver(function (entries) {
      entries.filter(function (entry) { return entry.isIntersecting; })
        .sort(function (a, b) {
          return (a.boundingClientRect.left - b.boundingClientRect.left) ||
            (a.boundingClientRect.top - b.boundingClientRect.top);
        })
        .forEach(function (entry) {
          const at = (window.performance && performance.now) ? performance.now() : Date.now();
          if (at - lastRevealAt > BATCH_GAP_MS) batchCount = 0;
          entry.target.style.setProperty('--reveal-delay', batchCount * STEP_MS + 'ms');
          entry.target.setAttribute('data-revealed', 'true');
          batchCount += 1;
          lastRevealAt = at;
          observer.unobserve(entry.target);
        });
    }, { threshold: 0.15, rootMargin: '0px 0px -5% 0px' });
    slides.forEach(function (slide) { observer.observe(slide); });
  }

  function initCarousel(root) {
    if (root.dataset.carouselReady === 'true') return;

    const el = collectElements(root);
    if (!el.track || el.slides.length === 0) return;

    const state = {
      current: 0,
      count: el.slides.length,
      slidesToShow: 1,
      circular: root.dataset.circular === 'true',
      fade: root.dataset.animation === 'fade',
      autoplay: root.dataset.autoplay === 'true' && !prefersReducedMotion(),
      interval: (parseFloat(root.dataset.autoplayInterval) || 5) * 1000,
      timer: null,
      playing: false,
      stopped: false,
      pointerX: null,
    };
    state.slidesToShow = applySlideCap(root, readSlidesToShow(root), state.count);
    state.maxIndex = Math.max(0, state.count - state.slidesToShow);

    function render() {
      if (state.fade) {
        el.slides.forEach(function (slide, i) {
          slide.setAttribute('data-active', i === state.current ? 'true' : 'false');
        });
      } else {
        el.track.style.transform = 'translateX(-' + state.current * (100 / state.slidesToShow) + '%)';
      }

      el.slides.forEach(function (slide, i) {
        const visible = state.fade
          ? i === state.current
          : i >= state.current && i < state.current + state.slidesToShow;
        slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });

      el.dots.forEach(function (dot, i) {
        const active = state.fade
          ? i === state.current
          : i >= state.current && i < state.current + state.slidesToShow;
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      if (!state.circular) {
        if (el.prev) el.prev.disabled = state.current === 0;
        if (el.next) el.next.disabled = state.current === state.maxIndex;
      }
    }

    function moveTo(index) {
      state.current = Math.min(Math.max(0, index), state.maxIndex);
      render();
    }

    function step(delta) {
      const next = state.current + delta;
      if (state.circular) {
        state.current = next < 0 ? state.maxIndex : (next > state.maxIndex ? 0 : next);
      } else {
        state.current = Math.min(state.maxIndex, Math.max(0, next));
      }
      render();
    }

    // Announcements are suppressed while the carousel moves on its own, per the
    // ARIA carousel pattern, and enabled once the visitor is driving it.
    function setLive(on) {
      el.track.setAttribute('aria-live', on ? 'polite' : 'off');
    }

    function play() {
      if (!state.autoplay || state.stopped || state.playing) return;
      state.playing = true;
      setLive(false);
      state.timer = window.setInterval(function () { step(1); }, state.interval);
    }

    // Suspend while the visitor is over or inside the carousel, then resume.
    function pause() {
      if (!state.playing) return;
      state.playing = false;
      setLive(true);
      window.clearInterval(state.timer);
      state.timer = null;
    }

    // Any deliberate navigation ends autoplay for good; resuming under someone
    // who just took control is the worst version of this pattern.
    function stop() {
      state.stopped = true;
      pause();
    }

    function syncResponsive() {
      const next = applySlideCap(root, readSlidesToShow(root), state.count);
      if (next === state.slidesToShow) return;
      state.slidesToShow = next;
      state.maxIndex = Math.max(0, state.count - next);
      if (state.current > state.maxIndex) state.current = state.maxIndex;
      render();
    }

    if (el.prev) el.prev.addEventListener('click', function () { stop(); step(-1); });
    if (el.next) el.next.addEventListener('click', function () { stop(); step(1); });

    el.dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { stop(); moveTo(i); });
    });

    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { stop(); step(-1); event.preventDefault(); }
      if (event.key === 'ArrowRight') { stop(); step(1); event.preventDefault(); }
    });

    if (el.viewport) {
      el.viewport.addEventListener('pointerdown', function (event) {
        state.pointerX = event.clientX;
      });
      el.viewport.addEventListener('pointerup', function (event) {
        if (state.pointerX === null) return;
        const dx = event.clientX - state.pointerX;
        state.pointerX = null;
        if (Math.abs(dx) < SWIPE_THRESHOLD) return;
        stop();
        step(dx < 0 ? 1 : -1);
      });
    }

    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', play);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', play);
    window.addEventListener('resize', syncResponsive);

    root.dataset.carouselReady = 'true';
    setLive(!state.autoplay);
    render();
    if (root.dataset.reveal === 'true') setupSlideReveal(el.slides);
    if (state.autoplay) play();
  }

  function init() {
    document.querySelectorAll('[data-carousel]').forEach(initCarousel);
  }

  document.addEventListener('DOMContentLoaded', init);
})();
